/**
 * Board — 3 Tables × 3 Lanes × 2 sides = 18 cells (D1, ui/31 §2). Each table
 * is rendered as a self-contained location: opponent row, front line, viewer
 * row. The grouping is presentation-only; canonical table×lane×side state and
 * placement semantics are unchanged.
 *
 * Its game-state projection stays pure: it reads `state.board.cells` /
 * `state.instances` and computes per-cell highlight/dim. The only effect
 * measures rendered cells to draw the UI-only combat pulse between them.
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, ReactElement, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import type { Action, CardInstance, CellId, GameState, InstanceId, PlayerId, Side, TableId } from '@cards/engine';
import { attackEligibility, cellId as makeCellId, effectiveStats, LANE_INDEXES, parseCellId, TABLE_IDS } from '@cards/engine';
import { registry } from '@cards/cards';
import type { CanApply } from '../types.js';
import type { PlayerNames } from './locale.js';
import { BoardCarryContext, type BoardCarryController, createBoardCarryGate } from './boardCarry.js';
import { Cell, type CellProps, type StagedLayerCardView } from './Cell.js';
import type { CardFlightApi } from './cardFlight.js';
import { CardGlyph } from './CardGlyph.js';
import { PixelIcon, type PixelIconName } from './PixelIcon.js';
import { FiberOverlay, type FiberOverlayLink } from './SpellPlan.js';
import { type CombatCue, type CombatStrike, TURN_PLAYBACK_TIMING, useTurnPlaybackSelector } from './TurnPlayback.js';
import { currentPlaybackTiming } from './playbackSpeed.js';
import { attachmentsFor, controllerOf, reservationForCell } from './m14View.js';
import { cardStatusViews } from './cardStatus.js';
import { gameCardUiContextFromRedactedSnapshot } from './cardUiAdapter.js';
import {
  cellEffects,
  locationEffectBadges,
  projectLocationEffectsFromRedactedSnapshot,
} from './locationEffectsModel.js';

// With the turn log removed on desktop the board owns the full column, so the
// cell width only has to reserve the board's own chrome. The 200px budget
// tallies every fixed horizontal cost between the viewport edge and the nine
// cells: game-main padding (32) + field padding (24) + two location gaps (52) +
// three locations' borders+padding (54) + per-location cell gaps (36). Under-
// reserving pushed the field past the column, where `overflow-x: hidden` clipped
// it. Mobile overrides `--cards-cell-width` outright, so this fallback is
// desktop-only.
const CELL_WIDTH = 'var(--cards-cell-width, clamp(80px, calc((100vw - 200px) / 9), 174px))';
const LOCATION_SYMBOLS: readonly PixelIconName[] = ['location0', 'location1', 'location2'];
// Per-fragment decay courier volley: three chips whose landing scatter, size
// and stagger differ so the flight reads as debris rather than a single dot.
// Fixed offsets on purpose — replays of the same turn render identically.
const COURIER_SCATTER = [
  { dx: -7, dy: -5, delayMs: 0, size: 7 },
  { dx: 6, dy: 7, delayMs: 70, size: 5 },
  { dx: -1, dy: -11, delayMs: 140, size: 6 },
] as const;

interface RectGeometry {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
}

interface PulseGeometry {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly angle: number;
  readonly targetLeft: number;
  readonly targetTop: number;
  /** Where the blow lands — see strikeContactPoint. The spark burst sits here. */
  readonly contactLeft: number;
  readonly contactTop: number;
}

/**
 * The slice of a snapshot one cell reads, resolved once per snapshot instead of
 * once per cell per render. Typed off CellProps so the single pass that builds it
 * and the render that spreads it can never drift apart; `visibleOccupantId` is
 * the extra the pass already knows and the render would otherwise re-derive.
 */
type CellStateView = Pick<
  CellProps,
  | 'instance'
  | 'effective'
  | 'cardType'
  | 'keywords'
  | 'rush'
  | 'shield'
  | 'eligibility'
  | 'controllerId'
  | 'attachments'
  | 'statuses'
  | 'effectBadges'
  | 'reservation'
> & { visibleOccupantId: InstanceId | null };

/** Translation that brings two axis-aligned card bounds to first contact. */
export function cardLungeVector(
  source: RectGeometry,
  target: RectGeometry,
  reciprocal: boolean,
): { dx: number; dy: number } {
  const sourceX = source.left + source.width / 2;
  const sourceY = source.top + source.height / 2;
  const targetX = target.left + target.width / 2;
  const targetY = target.top + target.height / 2;
  const centerDx = targetX - sourceX;
  const centerDy = targetY - sourceY;
  const length = Math.hypot(centerDx, centerDy);
  if (length === 0) return { dx: 0, dy: 0 };
  const unitX = centerDx / length;
  const unitY = centerDy / length;
  const horizontalContact = Math.abs(unitX) < Number.EPSILON
    ? Number.POSITIVE_INFINITY
    : (source.width + target.width) / 2 / Math.abs(unitX);
  const verticalContact = Math.abs(unitY) < Number.EPSILON
    ? Number.POSITIVE_INFINITY
    : (source.height + target.height) / 2 / Math.abs(unitY);
  const contactDistance = Math.min(horizontalContact, verticalContact);
  const totalTravel = Math.max(0, length - contactDistance);
  const travel = reciprocal ? totalTravel / 2 : totalTravel;
  return { dx: unitX * travel, dy: unitY * travel };
}

/**
 * Where a strike lands. Two cards lunging at each other each cover half the gap
 * and meet in the middle; a lone attacker stops at the target's facing edge; a
 * player panel is struck in its centre, since the attacker never reaches it.
 * The sparks fly from here rather than from the struck card's centre — a burst
 * in the middle of the card it hit reads as the card exploding, not as a blow.
 */
export function strikeContactPoint(
  source: RectGeometry,
  target: RectGeometry,
  mode: 'card' | 'reciprocal' | 'player',
): { left: number; top: number } {
  const sourceX = source.left + source.width / 2;
  const sourceY = source.top + source.height / 2;
  const targetX = target.left + target.width / 2;
  const targetY = target.top + target.height / 2;
  if (mode === 'player') return { left: targetX, top: targetY };
  if (mode === 'reciprocal') return { left: (sourceX + targetX) / 2, top: (sourceY + targetY) / 2 };
  const dx = targetX - sourceX;
  const dy = targetY - sourceY;
  const length = Math.hypot(dx, dy);
  if (length === 0) return { left: targetX, top: targetY };
  const unitX = dx / length;
  const unitY = dy / length;
  const reach = Math.min(
    Math.abs(unitX) < Number.EPSILON ? Number.POSITIVE_INFINITY : target.width / 2 / Math.abs(unitX),
    Math.abs(unitY) < Number.EPSILON ? Number.POSITIVE_INFINITY : target.height / 2 / Math.abs(unitY),
  );
  return { left: targetX - unitX * reach, top: targetY - unitY * reach };
}

/** Damage at which an ordinary hit already jolts the table. */
export const COMBAT_SHAKE_HEAVY_HIT = 4;

/**
 * How hard the table jolts on a combat impact. Heavy is reserved for what
 * changes the game — a card killed, a player hit — so it keeps meaning
 * something; a big hit gets a light jolt, and everything else, including the
 * zero-damage strike an aura plays its buff as, none at all.
 */
export function combatShake(
  cue: CombatCue | undefined,
  fracturedIds: readonly InstanceId[],
): 'heavy' | 'light' | undefined {
  if (cue === undefined || cue.phase !== 'impact') return undefined;
  const strikes: CombatStrike[] = [cue];
  if (cue.reciprocal !== undefined) strikes.push(cue.reciprocal);
  if (cue.parallel !== undefined) strikes.push(cue.parallel);
  const landed = strikes.filter((strike) => strike.amount > 0);
  if (landed.some((strike) => strike.targetPlayerId !== undefined
    || damageMagnitude(strike.amount) === 4
    || (strike.targetInstanceId !== undefined && fracturedIds.includes(strike.targetInstanceId)))) {
    return 'heavy';
  }
  return landed.some((strike) => strike.amount >= COMBAT_SHAKE_HEAVY_HIT) ? 'light' : undefined;
}

/** Size class of a damage number, so a big hit is read before it is counted. */
export function damageMagnitude(amount: number): 1 | 2 | 3 | 4 {
  if (amount >= 8) return 4;
  if (amount >= 5) return 3;
  if (amount >= 3) return 2;
  return 1;
}

/**
 * The impact burst: hard pixel chips sprayed forward from the contact point (the
 * ring element is turned onto the strike's angle, so "forward" is its local +x).
 * Where each chip flies is set in the stylesheet, one rule per child.
 */
function ImpactSparks(): ReactElement {
  return <><span /><span /><span /><span /><span /><span /><span /><span /></>;
}

/** The donor finishes at the receiver's centre before its consume removes it. */
export function cardAbsorptionVector(source: RectGeometry, receiver: RectGeometry): { dx: number; dy: number } {
  return {
    dx: receiver.left + receiver.width / 2 - (source.left + source.width / 2),
    dy: receiver.top + receiver.height / 2 - (source.top + source.height / 2),
  };
}

export interface BoardProps {
  state: GameState;
  viewerId: PlayerId;
  viewerSide: Side;
  opponentSide: Side;
  /** Currently selected hand card instance (drives placement-target highlighting). */
  selectedHandId: InstanceId | undefined;
  /** Hand card currently carried by the shared pointer-drag interaction. */
  draggingHandId: InstanceId | undefined;
  /** Own staged card currently dragged toward another cell or back to the hand. */
  draggingStagedId: InstanceId | undefined;
  playerNames?: PlayerNames | undefined;
  /** Visible staged FIELD intents, preserving committed order within each cell. */
  stagedByCell: Partial<Record<CellId, readonly InstanceId[]>>;
  /** Trusted open-information surfaces (sandbox) may also show the opponent's staged cards. */
  showOpponentStaged?: boolean;
  planningDisabled: boolean;
  canApply: CanApply | undefined;
  onCellClick: (cellId: CellId) => void;
  onStagedPointerDragStart: (instanceId: InstanceId) => void;
  onStagedPointerDrop: (instanceId: InstanceId, clientX: number, clientY: number) => void;
  onStagedPointerDragEnd: () => void;
  onStagedKeyboardUnplan: (instanceId: InstanceId) => void;
  intentForCell: (instanceId: InstanceId, cellId: CellId) => { selectable: boolean; action?: Action };
  /** Whole-location placement affordance for location-targeting cards (slimefall/contamination_map). */
  intentForTable: (instanceId: InstanceId, tableId: TableId) => { selectable: boolean; action?: Action };
  onTableClick: (tableId: TableId) => void;
  /** Active multi-step board-targeting session (foreign_flesh/pull_into_clot): candidate + picked cells. */
  targeting?: { candidateCells: ReadonlySet<CellId>; selectedCells: ReadonlySet<CellId> } | undefined;
  onTargetingCellClick: (cellId: CellId) => void;
  /** Shared flight system: released staged cards fly to their new place. */
  flights: CardFlightApi;
  /** Visual one-shot rails live in the field layout but remain route-owned. */
  opponentSpellQueue?: ReactNode;
  viewerSpellQueue?: ReactNode;
}

interface LocationGroupProps {
  readonly tableId: TableId;
  readonly tableIndex: number;
  readonly side: 'opponent' | 'viewer';
  readonly children: readonly ReactElement[];
}

/**
 * Row layout inside a location. Location targeting belongs to the enclosing
 * section, so the opponent and viewer rows cannot read as separate targets.
 */
function LocationGroup({
  tableId,
  tableIndex,
  side,
  children,
}: LocationGroupProps): ReactElement {
  return (
    <div
      className="cards-board-group"
      data-testid={`board-group-${side}-${tableId}`}
      data-table-id={tableId}
      data-first={tableIndex === 0 ? 'true' : undefined}
    >
      {children}
    </div>
  );
}

export function Board(props: BoardProps): ReactElement {
  // Per-cell callbacks are reached through latestCellPropsRef below, so their
  // identities can stay stable for memo(Cell); only the rest is destructured.
  const { state, viewerSide, opponentSide, selectedHandId, draggingHandId, draggingStagedId, playerNames, stagedByCell, planningDisabled, canApply, intentForCell, intentForTable, onTableClick, targeting, flights } =
    props;
  // Only the cues the board itself presents: notices/banners and per-card
  // animations flow elsewhere, so those steps skip re-rendering 18 cells.
  const playback = useTurnPlaybackSelector((visuals) => ({
    combatCue: visuals.combatCue,
    effectPathCue: visuals.effectPathCue,
    castCue: visuals.castCue,
    cellCue: visuals.cellCue,
    moveCue: visuals.moveCue,
    spawnCue: visuals.spawnCue,
    assemblyCue: visuals.assemblyCue,
    fieldFizzleCue: visuals.fieldFizzleCue,
    enteringId: visuals.enteringId,
    departedIds: visuals.departedIds,
    withheldFieldEntryIds: visuals.withheldFieldEntryIds,
    // Graded here rather than by reading fracturedIds, so a fracture elsewhere
    // on the board does not re-render it: only the jolt level is compared.
    shake: combatShake(visuals.combatCue, visuals.fracturedIds),
  }));
  // The carry flag, kept OUT of the cells' props on purpose — see ./boardCarry.ts
  // for why a pickup and a release used to re-render all 36 board components. The
  // box is created once and never replaced, so publishing it costs no consumer a
  // render; the effect below is what flips it after the commit that started or
  // ended the carry, which is before any pointer event can ask.
  const carryGateRef = useRef<BoardCarryController | undefined>(undefined);
  carryGateRef.current ??= createBoardCarryGate();
  const carryGate = carryGateRef.current;
  const carrying = draggingHandId !== undefined || draggingStagedId !== undefined;
  useLayoutEffect(() => {
    carryGate.set(carrying);
  }, [carryGate, carrying]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const fieldRef = useRef<HTMLDivElement>(null);
  const cellAnchor = (cellId: CellId): Element | null =>
    fieldRef.current?.querySelector(`[data-testid="cell-${cellId}"]`) ?? null;
  // One pass over the snapshot for everything the 18 cells read out of it.
  //
  // These queries used to run inline in renderCell, i.e. 18× per Board render —
  // and Board re-renders on every gesture step too (a carried card flips
  // highlight/dim on all of them), where the state has not changed at all. The
  // shield scan was the worst of it: a full walk of state.effects per cell, so
  // O(18 × effects) for a total that one O(effects) pass answers for everyone.
  const cellStateViews = useMemo(() => {
    const hidden = new Set<InstanceId>([
      ...playback.departedIds,
      ...playback.withheldFieldEntryIds,
    ]);
    const shields = new Map<InstanceId, number>();
    for (const effect of state.effects) {
      if (effect.scope.kind !== 'unit') continue;
      const remaining = effect.shield?.remaining ?? 0;
      if (remaining === 0) continue;
      const { instanceId } = effect.scope;
      shields.set(instanceId, (shields.get(instanceId) ?? 0) + remaining);
    }
    const views = new Map<CellId, CellStateView>();
    for (const tableId of TABLE_IDS) {
      const locationEffects = projectLocationEffectsFromRedactedSnapshot(state, props.viewerId, tableId);
      for (const laneIndex of LANE_INDEXES) {
        for (const side of [viewerSide, opponentSide] as const) {
          const id = makeCellId(tableId, laneIndex, side);
          const occupantId = state.board.cells[id]?.occupantId ?? null;
          const visibleOccupantId = occupantId !== null && hidden.has(occupantId) ? null : occupantId;
          const instance: CardInstance | undefined =
            visibleOccupantId !== null ? state.instances[visibleOccupantId] : undefined;
          const definition = instance === undefined ? undefined : registry.get(instance.defId);
          const effectBadges = locationEffectBadges(cellEffects(
            locationEffects,
            laneIndex,
            side === viewerSide ? 'viewer' : 'opponent',
          ));
          const cardUiContext = visibleOccupantId === null
            ? undefined
            : gameCardUiContextFromRedactedSnapshot(state, visibleOccupantId, props.viewerId);
          // Badge-carrying location effects surface through the combined cell-state
          // card (Cell/BoardCard details), so the occupant's own status list skips
          // them — otherwise every preview would describe the same effect twice.
          const badgeDefinitionIds = new Set(effectBadges.map((badge) => badge.definitionId));
          const statusContext = cardUiContext === undefined || badgeDefinitionIds.size === 0
            ? cardUiContext
            : {
              ...cardUiContext,
              visibleEffects: cardUiContext.visibleEffects.filter(
                (effect) => !badgeDefinitionIds.has(effect.definitionId),
              ),
            };
          views.set(id, {
            visibleOccupantId,
            instance,
            effective: visibleOccupantId === null ? undefined : effectiveStats(state, visibleOccupantId),
            cardType: definition?.type,
            keywords: definition?.keywords ?? [],
            rush: definition?.keywords?.includes('RUSH') === true,
            shield: visibleOccupantId === null ? 0 : shields.get(visibleOccupantId) ?? 0,
            eligibility: visibleOccupantId === null
              ? undefined
              : attackEligibility(state, registry, visibleOccupantId),
            controllerId: instance === undefined ? undefined : controllerOf(instance),
            attachments: visibleOccupantId === null ? [] : attachmentsFor(state, visibleOccupantId),
            statuses: statusContext === undefined ? [] : cardStatusViews(statusContext),
            effectBadges,
            reservation: reservationForCell(state, id),
          });
        }
      }
    }
    return views;
  }, [state, props.viewerId, viewerSide, opponentSide, playback.departedIds, playback.withheldFieldEntryIds]);
  // Pulse/cast geometry deliberately lives OUTSIDE React state: the capture
  // scroll/resize listeners fire per frame during the playback auto-scroll,
  // and routing them through setState re-rendered all 18 cells on every
  // event. React only tracks whether the anchors were found (portal mount);
  // coordinates are written straight to the portal nodes below.
  const [pulseMeasured, setPulseMeasured] = useState(false);
  const [parallelPulseMeasured, setParallelPulseMeasured] = useState(false);
  const [castMeasured, setCastMeasured] = useState(false);
  // Fibers resolve their own anchors every frame, so both cues only have to
  // name the cells: no measuring, and no setState riding the scroll listener.
  const devourFibers = useMemo<readonly FiberOverlayLink[]>(() => {
    const cue = playback.effectPathCue;
    if (cue?.kind !== 'devour' || cue.targetCellId === undefined) return [];
    const { sourceCellId, targetCellId } = cue;
    // Both ends grip the CARDS, not their cells: the vines then shorten with
    // the prey as it is dragged home instead of pointing at the hole it left.
    const cardAnchor = (cellId: CellId): Element | null => {
      const cell = cellAnchor(cellId);
      return cell?.querySelector('[data-testid^="board-card-"]') ?? cell;
    };
    return [{
      key: `${sourceCellId}:${targetCellId}`,
      colorIndex: 3,
      variant: 'devour',
      revealMs: currentPlaybackTiming().devour,
      source: () => cardAnchor(sourceCellId),
      target: () => cardAnchor(targetCellId),
    }];
  }, [playback.effectPathCue]);
  // The thread the player drew during planning is the thread that fires: the
  // cast reuses the same renderer, growing out of the queue orb with a spark
  // at its tip instead of the target simply being stamped from nowhere.
  const castFibers = useMemo<readonly FiberOverlayLink[]>(() => {
    const cue = playback.castCue;
    if (cue === undefined || cue.sourceInstanceId === undefined) return [];
    const { sourceInstanceId, targetCellId, targetPlayerId } = cue;
    if (targetCellId === undefined && targetPlayerId === undefined) return [];
    return [{
      key: `cast:${sourceInstanceId}`,
      colorIndex: 2,
      variant: 'cast',
      phase: cue.phase,
      revealMs: currentPlaybackTiming().effectTravel,
      source: () => document.querySelector(
        `[data-spell-queue-marker="${sourceInstanceId.replace(/["\\]/gu, '\\$&')}"]`),
      target: () => targetPlayerId !== undefined
        ? document.querySelector(`[data-player-id="${targetPlayerId.replace(/["\\]/gu, '\\$&')}"]`)
        : targetCellId === undefined ? null : cellAnchor(targetCellId),
    }];
  }, [playback.castCue]);
  const pulseGeometryRef = useRef<PulseGeometry | undefined>(undefined);
  const parallelPulseGeometryRef = useRef<PulseGeometry | undefined>(undefined);
  const castPositionRef = useRef<{ left: number; top: number } | undefined>(undefined);
  const pulseRef = useRef<HTMLDivElement | null>(null);
  const parallelPulseRef = useRef<HTMLDivElement | null>(null);
  // Cells styled with the measured lunge/knockback vectors, kept for cleanup.
  const lungeStyledRef = useRef<HTMLElement[]>([]);
  const absorptionStyledRef = useRef<HTMLElement[]>([]);
  const devourStyledRef = useRef<HTMLElement[]>([]);
  // Cells and locations lifted out of their own clipping/stacking for the
  // duration of a card-to-card flight, kept for cleanup.
  const flightStyledRef = useRef<HTMLElement[]>([]);
  const effectLinkRef = useRef<HTMLDivElement | null>(null);
  const impactRingRef = useRef<HTMLDivElement | null>(null);
  const damageNumberRef = useRef<HTMLDivElement | null>(null);
  const reciprocalImpactRingRef = useRef<HTMLDivElement | null>(null);
  const reciprocalDamageNumberRef = useRef<HTMLDivElement | null>(null);
  const parallelImpactRingRef = useRef<HTMLDivElement | null>(null);
  const parallelDamageNumberRef = useRef<HTMLDivElement | null>(null);
  const castRef = useRef<HTMLDivElement | null>(null);
  const spawnStyledRef = useRef<HTMLElement[]>([]);
  const assemblyStyledRef = useRef<HTMLElement[]>([]);
  // Decay burst debris: a pixel-chip burst at the dying source's cell. A few
  // chips crack radially in place, and per fragment a courier volley flies the
  // actual source→target vector so the eye is carried to the cell where the
  // fragment card then condenses. Measured by the spawn-cue effect below.
  const [debrisGeometry, setDebrisGeometry] = useState<{
    left: number;
    top: number;
    couriers: readonly { dx: number; dy: number; delayMs: number; size: number }[];
  } | undefined>(undefined);
  const decayCellStyledRef = useRef<HTMLElement[]>([]);

  // Layout effect on purpose: the same commit that inserts the fragment cards
  // flips their cells to occupied chrome, so the decay-receive mask below has
  // to land before that frame paints or the "filled" frame flashes once.
  useLayoutEffect(() => {
    const root = fieldRef.current;
    const cue = playback.spawnCue;
    const clear = (): void => {
      for (const element of spawnStyledRef.current) {
        element.classList.remove('cards-board-card-death-spawn');
        element.classList.remove('cards-board-card-decay-fragment-spawn');
        element.style.removeProperty('--cards-spawn-dx');
        element.style.removeProperty('--cards-spawn-dy');
      }
      spawnStyledRef.current = [];
      for (const element of decayCellStyledRef.current) {
        element.classList.remove('cards-board-cell-decay-receive');
      }
      decayCellStyledRef.current = [];
    };
    clear();
    setDebrisGeometry(undefined);
    if (root === null || cue === undefined) return clear;
    const source = root.querySelector<HTMLElement>(`[data-testid="cell-${cue.sourceCellId}"]`);
    if (source === null) return clear;
    const sourceRect = source.getBoundingClientRect();
    const couriers: { dx: number; dy: number; delayMs: number; size: number }[] = [];
    for (const instanceId of cue.targetInstanceIds) {
      const token = root.querySelector<HTMLElement>(`[data-testid="board-card-${instanceId}"]`);
      if (token === null) continue;
      const vector = cardAbsorptionVector(token.getBoundingClientRect(), sourceRect);
      token.style.setProperty('--cards-spawn-dx', `${vector.dx}px`);
      token.style.setProperty('--cards-spawn-dy', `${vector.dy}px`);
      token.classList.add('cards-board-card-death-spawn');
      if (cue.decayFragment === true) {
        token.classList.add('cards-board-card-decay-fragment-spawn');
        // Couriers fly the reverse of the card's approach vector: from the
        // source centre out to the fragment's cell.
        for (const scatter of COURIER_SCATTER) {
          couriers.push({
            dx: -vector.dx + scatter.dx,
            dy: -vector.dy + scatter.dy,
            delayMs: scatter.delayMs,
            size: scatter.size,
          });
        }
        const cell = token.closest<HTMLElement>('.cards-board-cell');
        if (cell !== null) {
          cell.classList.add('cards-board-cell-decay-receive');
          decayCellStyledRef.current.push(cell);
        }
      }
      spawnStyledRef.current.push(token);
    }
    if (cue.decayFragment === true) {
      setDebrisGeometry({
        left: sourceRect.left + sourceRect.width / 2,
        top: sourceRect.top + sourceRect.height / 2,
        couriers,
      });
    }
    return clear;
  }, [playback.spawnCue, state.instances]);

  useEffect(() => {
    const root = fieldRef.current;
    const cue = playback.assemblyCue;
    const clear = (): void => {
      for (const element of assemblyStyledRef.current) {
        element.classList.remove('cards-board-card-assembly-donor');
        element.style.removeProperty('--cards-assembly-dx');
        element.style.removeProperty('--cards-assembly-dy');
      }
      assemblyStyledRef.current = [];
    };
    clear();
    if (root === null || cue === undefined) return clear;
    const target = root.querySelector<HTMLElement>(`[data-testid="cell-${cue.robotCellId}"]`);
    if (target === null) return clear;
    for (const fragmentId of cue.fragmentIds) {
      const fragment = root.querySelector<HTMLElement>(`[data-testid="board-card-${fragmentId}"]`);
      if (fragment === null) continue;
      const vector = cardAbsorptionVector(fragment.getBoundingClientRect(), target.getBoundingClientRect());
      fragment.style.setProperty('--cards-assembly-dx', `${vector.dx}px`);
      fragment.style.setProperty('--cards-assembly-dy', `${vector.dy}px`);
      fragment.classList.add('cards-board-card-assembly-donor');
      assemblyStyledRef.current.push(fragment);
    }
    return clear;
  }, [playback.assemblyCue]);

  function applyPulseGeometry(): void {
    const geometry = pulseGeometryRef.current;
    if (geometry === undefined) return;
    for (const element of [pulseRef.current, effectLinkRef.current]) {
      if (element === null) continue;
      const deathFall = element.dataset.kind === 'death-fall';
      element.style.left = `${deathFall ? geometry.targetLeft : geometry.left}px`;
      element.style.top = `${deathFall ? geometry.targetTop : geometry.top}px`;
      element.style.width = `${deathFall ? 0 : geometry.width}px`;
      element.style.setProperty('--cards-combat-angle', `${geometry.angle}deg`);
    }
    placeSparks(impactRingRef.current, geometry.contactLeft, geometry.contactTop, geometry.angle);
    placeNumber(damageNumberRef.current, geometry.targetLeft, geometry.targetTop, 1);
    // Both cards meet at one point, so the facing strike bursts from the same
    // spot the other way: the two sprays together are the clash.
    placeSparks(reciprocalImpactRingRef.current, geometry.contactLeft, geometry.contactTop, geometry.angle + 180);
    placeNumber(reciprocalDamageNumberRef.current, geometry.left, geometry.top, -1);
    const parallelGeometry = parallelPulseGeometryRef.current;
    if (parallelGeometry === undefined) return;
    if (parallelPulseRef.current !== null) {
      parallelPulseRef.current.style.left = `${parallelGeometry.left}px`;
      parallelPulseRef.current.style.top = `${parallelGeometry.top}px`;
      parallelPulseRef.current.style.width = `${parallelGeometry.width}px`;
      parallelPulseRef.current.style.setProperty('--cards-combat-angle', `${parallelGeometry.angle}deg`);
    }
    placeSparks(parallelImpactRingRef.current, parallelGeometry.contactLeft, parallelGeometry.contactTop, parallelGeometry.angle);
    placeNumber(parallelDamageNumberRef.current, parallelGeometry.targetLeft, parallelGeometry.targetTop, 1);
  }

  function placeSparks(element: HTMLElement | null, left: number, top: number, angle: number): void {
    if (element === null) return;
    element.style.left = `${left}px`;
    element.style.top = `${top}px`;
    element.style.setProperty('--cards-combat-angle', `${angle}deg`);
  }

  // drift: which way the number tumbles off, so a clash's two numbers part.
  function placeNumber(element: HTMLElement | null, left: number, top: number, drift: 1 | -1): void {
    if (element === null) return;
    element.style.left = `${left}px`;
    element.style.top = `${top}px`;
    element.style.setProperty('--cards-number-drift', String(drift));
  }

  function applyCastGeometry(): void {
    const position = castPositionRef.current;
    const element = castRef.current;
    if (position === undefined || element === null) return;
    element.style.left = `${position.left}px`;
    element.style.top = `${position.top}px`;
  }

  useEffect(() => {
    const root = fieldRef.current;
    const cue = playback.combatCue ?? playback.effectPathCue;
    const clearLungeVariables = (): void => {
      for (const element of lungeStyledRef.current) {
        element.style.removeProperty('--cards-lunge-dx');
        element.style.removeProperty('--cards-lunge-dy');
        element.style.removeProperty('--cards-lunge-ux');
        element.style.removeProperty('--cards-lunge-uy');
        element.style.removeProperty('--cards-hit-dx');
        element.style.removeProperty('--cards-hit-dy');
        element.style.removeProperty('z-index');
      }
      lungeStyledRef.current = [];
    };
    const clearAbsorptionClasses = (): void => {
      for (const element of absorptionStyledRef.current) {
        element.classList.remove('cards-board-card-absorb-donor', 'cards-board-card-absorb-receiver');
        element.style.removeProperty('--cards-absorb-dx');
        element.style.removeProperty('--cards-absorb-dy');
      }
      absorptionStyledRef.current = [];
    };
    const clearDevourClasses = (): void => {
      for (const element of devourStyledRef.current) {
        element.classList.remove('cards-board-card-devour-prey', 'cards-board-card-devourer');
        element.style.removeProperty('--cards-devour-dx');
        element.style.removeProperty('--cards-devour-dy');
      }
      devourStyledRef.current = [];
    };
    const clearFlightLift = (): void => {
      for (const element of flightStyledRef.current) {
        element.classList.remove('cards-board-location-flight');
        element.style.removeProperty('z-index');
      }
      flightStyledRef.current = [];
    };
    // A card travelling between cells is clipped by its location frame
    // (overflow: hidden) and trapped in its cell's stacking context
    // (isolation: isolate), so it paints UNDER the slots it flies across and is
    // cut off at the arena border. Lift both boxes for the length of the cue;
    // the donor/prey end is lifted higher so it stays on top of the end that
    // swallows it. Same problem the attack lunge solves for combat below.
    // Lifts the location frame around `cell` only. Registration is idempotent
    // (a scroll/resize re-measure calls this again) while the z-index is not:
    // the LAST caller wins, so the travelling end is passed last.
    const liftLocation = (cell: HTMLElement, locationZ: number): void => {
      const location = cell.closest<HTMLElement>('.cards-board-location');
      if (location === null) return;
      location.classList.add('cards-board-location-flight');
      location.style.zIndex = String(locationZ);
      if (!flightStyledRef.current.includes(location)) flightStyledRef.current.push(location);
    };
    const liftFlight = (cell: HTMLElement, cellZ: number, locationZ: number): void => {
      cell.style.zIndex = String(cellZ);
      flightStyledRef.current.push(cell);
      liftLocation(cell, locationZ);
    };
    // A strike between two arenas has the same problem as the flights above: the
    // lunge rides on the live card, so it leaves its own location box — which
    // CLIPS (overflow: hidden) and paints in DOM order. The attacker was cut at
    // the border and slid UNDER the locations between it and its target, most
    // visibly once portrait phones stacked the three arenas down the screen.
    // Only cross-arena strikes lift: inside one location nothing leaves the
    // frame, so reordering the arenas there would buy nothing.
    const liftCombatLocations = (source: HTMLElement, target: HTMLElement): void => {
      if (source.closest('.cards-board-location') === target.closest('.cards-board-location')) return;
      // Target first — the attacker is the end that travels, so its lift wins.
      liftLocation(target, 4);
      liftLocation(source, 5);
    };
    if (root === null || cue === undefined) {
      pulseGeometryRef.current = undefined;
      parallelPulseGeometryRef.current = undefined;
      setPulseMeasured(false);
      setParallelPulseMeasured(false);
      clearAbsorptionClasses();
      clearDevourClasses();
      clearFlightLift();
      return;
    }
    const updateGeometry = (): void => {
      const currentSource = root.querySelector<HTMLElement>(`[data-testid="cell-${cue.sourceCellId}"]`);
      const currentTarget = cue.targetPlayerId === undefined
        ? cue.targetCellId === undefined
          ? null
          : root.querySelector<HTMLElement>(`[data-testid="cell-${cue.targetCellId}"]`)
        : document.querySelector<HTMLElement>(`[data-player-id="${cue.targetPlayerId}"]`);
      if (currentSource === null || currentTarget === null) {
        pulseGeometryRef.current = undefined;
        parallelPulseGeometryRef.current = undefined;
        clearLungeVariables();
        // Devour/absorb classes are NOT cleared here for the same reason they
        // are not re-stamped below: dropping them on a re-measure and letting
        // the guard re-add them on the next one restarts the keyframe from 0%
        // and flashes the prey back to full size. The effect cleanup owns them.
        setPulseMeasured(false);
        setParallelPulseMeasured(false);
        return;
      }
      const sourceRect = currentSource.getBoundingClientRect();
      const targetRect = currentTarget.getBoundingClientRect();
      const sourceX = sourceRect.left + sourceRect.width / 2;
      const sourceY = sourceRect.top + sourceRect.height / 2;
      const targetX = targetRect.left + targetRect.width / 2;
      const targetY = targetRect.top + targetRect.height / 2;
      const contactTargetCard = cue.targetPlayerId === undefined
        ? currentTarget.querySelector<HTMLElement>('[data-testid^="board-card-"]')
        : null;
      // Measured at the impact phase the struck card is still at rest: its
      // knockback waits out the hit-stop, so this rect is the card it shows.
      const contact = strikeContactPoint(
        sourceRect,
        contactTargetCard?.getBoundingClientRect() ?? targetRect,
        cue.targetPlayerId !== undefined
          ? 'player'
          : playback.combatCue?.reciprocal !== undefined ? 'reciprocal' : 'card',
      );
      pulseGeometryRef.current = {
        left: sourceX,
        top: sourceY,
        width: Math.hypot(targetX - sourceX, targetY - sourceY),
        angle: Math.atan2(targetY - sourceY, targetX - sourceX) * 180 / Math.PI,
        targetLeft: targetX,
        targetTop: targetY,
        contactLeft: contact.left,
        contactTop: contact.top,
      };
      const parallelCue = playback.combatCue?.parallel;
      let parallelAnchors: { source: HTMLElement; target: HTMLElement; sourceRect: DOMRect; targetRect: DOMRect } | undefined;
      if (parallelCue !== undefined) {
        const parallelSource = root.querySelector<HTMLElement>(`[data-testid="cell-${parallelCue.sourceCellId}"]`);
        const parallelTarget = parallelCue.targetPlayerId === undefined
          ? parallelCue.targetCellId === undefined
            ? null
            : root.querySelector<HTMLElement>(`[data-testid="cell-${parallelCue.targetCellId}"]`)
          : document.querySelector<HTMLElement>(`[data-player-id="${parallelCue.targetPlayerId}"]`);
        if (parallelSource === null || parallelTarget === null) {
          parallelPulseGeometryRef.current = undefined;
          setParallelPulseMeasured(false);
        } else {
          const parallelSourceRect = parallelSource.getBoundingClientRect();
          const parallelTargetRect = parallelTarget.getBoundingClientRect();
          const parallelSourceX = parallelSourceRect.left + parallelSourceRect.width / 2;
          const parallelSourceY = parallelSourceRect.top + parallelSourceRect.height / 2;
          const parallelTargetX = parallelTargetRect.left + parallelTargetRect.width / 2;
          const parallelTargetY = parallelTargetRect.top + parallelTargetRect.height / 2;
          const parallelContactCard = parallelCue.targetPlayerId === undefined
            ? parallelTarget.querySelector<HTMLElement>('[data-testid^="board-card-"]')
            : null;
          const parallelContact = strikeContactPoint(
            parallelSourceRect,
            parallelContactCard?.getBoundingClientRect() ?? parallelTargetRect,
            parallelCue.targetPlayerId === undefined ? 'card' : 'player',
          );
          parallelPulseGeometryRef.current = {
            left: parallelSourceX,
            top: parallelSourceY,
            width: Math.hypot(parallelTargetX - parallelSourceX, parallelTargetY - parallelSourceY),
            angle: Math.atan2(parallelTargetY - parallelSourceY, parallelTargetX - parallelSourceX) * 180 / Math.PI,
            targetLeft: parallelTargetX,
            targetTop: parallelTargetY,
            contactLeft: parallelContact.left,
            contactTop: parallelContact.top,
          };
          parallelAnchors = {
            source: parallelSource,
            target: parallelTarget,
            sourceRect: parallelSourceRect,
            targetRect: parallelTargetRect,
          };
          setParallelPulseMeasured(true);
        }
      } else {
        parallelPulseGeometryRef.current = undefined;
        setParallelPulseMeasured(false);
      }
      // The attack lunge rides on the live board card, so the measured attack
      // vector is stamped onto the source/target cells as custom properties —
      // the same outside-React contract as the pulse geometry above.
      // Devour/absorb classes are deliberately NOT cleared here: a re-measure
      // (scroll/resize mid-cue) that removed and re-added them would restart
      // the keyframes from 0% — with per-frame smooth auto-scroll that froze
      // the animation on its first frame. They are applied once per cue below
      // and removed by the effect cleanup.
      clearLungeVariables();
      if (playback.combatCue !== undefined) {
        const centerDx = targetX - sourceX;
        const centerDy = targetY - sourceY;
        const length = Math.hypot(centerDx, centerDy) || 1;
        const sourceCard = currentSource.querySelector<HTMLElement>('[data-testid^="board-card-"]');
        const targetCard = currentTarget.querySelector<HTMLElement>('[data-testid^="board-card-"]');
        const vector = playback.combatCue.targetPlayerId === undefined
          ? cardLungeVector(
            sourceCard?.getBoundingClientRect() ?? sourceRect,
            targetCard?.getBoundingClientRect() ?? targetRect,
            playback.combatCue.reciprocal !== undefined,
          )
          : { dx: centerDx / length * 36, dy: centerDy / length * 36 };
        currentSource.style.setProperty('--cards-lunge-dx', `${vector.dx}px`);
        currentSource.style.setProperty('--cards-lunge-dy', `${vector.dy}px`);
        // The wind-up is a fixed pull-back along this direction, not a share of
        // the travel: facing rows stand ~20px apart, so a proportional wind-up
        // came to a pixel or two and never read.
        currentSource.style.setProperty('--cards-lunge-ux', String(centerDx / length));
        currentSource.style.setProperty('--cards-lunge-uy', String(centerDy / length));
        // Cells isolate their own stacking, so lift the whole cell above its
        // siblings or the travelling card paints under neighbour slots.
        currentSource.style.zIndex = '6';
        lungeStyledRef.current.push(currentSource);
        if (playback.combatCue.reciprocal !== undefined) {
          currentTarget.style.setProperty('--cards-lunge-dx', `${-vector.dx}px`);
          currentTarget.style.setProperty('--cards-lunge-dy', `${-vector.dy}px`);
          currentTarget.style.setProperty('--cards-lunge-ux', String(-centerDx / length));
          currentTarget.style.setProperty('--cards-lunge-uy', String(-centerDy / length));
          currentTarget.style.zIndex = '6';
          lungeStyledRef.current.push(currentTarget);
        } else if (playback.combatCue.targetPlayerId === undefined) {
          currentTarget.style.setProperty('--cards-hit-dx', `${centerDx / length * 12}px`);
          currentTarget.style.setProperty('--cards-hit-dy', `${centerDy / length * 12}px`);
          currentTarget.style.zIndex = '5';
          lungeStyledRef.current.push(currentTarget);
        }
        liftCombatLocations(currentSource, currentTarget);
        if (parallelCue !== undefined && parallelAnchors !== undefined) {
          const parallelDx = parallelAnchors.targetRect.left + parallelAnchors.targetRect.width / 2
            - (parallelAnchors.sourceRect.left + parallelAnchors.sourceRect.width / 2);
          const parallelDy = parallelAnchors.targetRect.top + parallelAnchors.targetRect.height / 2
            - (parallelAnchors.sourceRect.top + parallelAnchors.sourceRect.height / 2);
          const parallelLength = Math.hypot(parallelDx, parallelDy) || 1;
          const parallelSourceCard = parallelAnchors.source.querySelector<HTMLElement>('[data-testid^="board-card-"]');
          const parallelTargetCard = parallelAnchors.target.querySelector<HTMLElement>('[data-testid^="board-card-"]');
          const parallelVector = parallelCue.targetPlayerId === undefined
            ? cardLungeVector(
              parallelSourceCard?.getBoundingClientRect() ?? parallelAnchors.sourceRect,
              parallelTargetCard?.getBoundingClientRect() ?? parallelAnchors.targetRect,
              false,
            )
            : { dx: parallelDx / parallelLength * 36, dy: parallelDy / parallelLength * 36 };
          parallelAnchors.source.style.setProperty('--cards-lunge-dx', `${parallelVector.dx}px`);
          parallelAnchors.source.style.setProperty('--cards-lunge-dy', `${parallelVector.dy}px`);
          parallelAnchors.source.style.setProperty('--cards-lunge-ux', String(parallelDx / parallelLength));
          parallelAnchors.source.style.setProperty('--cards-lunge-uy', String(parallelDy / parallelLength));
          parallelAnchors.source.style.zIndex = '6';
          lungeStyledRef.current.push(parallelAnchors.source);
          if (parallelCue.targetPlayerId === undefined) {
            parallelAnchors.target.style.setProperty('--cards-hit-dx', `${parallelDx / parallelLength * 12}px`);
            parallelAnchors.target.style.setProperty('--cards-hit-dy', `${parallelDy / parallelLength * 12}px`);
            parallelAnchors.target.style.zIndex = '5';
            lungeStyledRef.current.push(parallelAnchors.target);
          }
          liftCombatLocations(parallelAnchors.source, parallelAnchors.target);
        }
      }
      if (playback.effectPathCue?.kind === 'absorb') {
        const donorCard = currentSource.querySelector<HTMLElement>('[data-testid^="board-card-"]');
        const receiverCard = currentTarget.querySelector<HTMLElement>('[data-testid^="board-card-"]');
        // Vector and classes are stamped once per cue: the card rects of a
        // re-measure are mid-flight (transformed), so recomputing from them
        // would corrupt the travel vector, and scrolling moves both ends
        // equally — the relative vector never changes.
        if (donorCard !== null && receiverCard !== null
          && !donorCard.classList.contains('cards-board-card-absorb-donor')) {
          const vector = cardAbsorptionVector(donorCard.getBoundingClientRect(), receiverCard.getBoundingClientRect());
          donorCard.style.setProperty('--cards-absorb-dx', `${vector.dx}px`);
          donorCard.style.setProperty('--cards-absorb-dy', `${vector.dy}px`);
          donorCard.classList.add('cards-board-card-absorb-donor');
          receiverCard.classList.add('cards-board-card-absorb-receiver');
          absorptionStyledRef.current.push(donorCard, receiverCard);
          // Receiver first: when both ends share a location the donor's higher
          // lift is the one that sticks.
          liftFlight(currentTarget, 7, 4);
          liftFlight(currentSource, 9, 5);
        }
      }
      if (playback.effectPathCue?.kind === 'devour') {
        const devourerCard = currentSource.querySelector<HTMLElement>('[data-testid^="board-card-"]');
        const preyCard = currentTarget.querySelector<HTMLElement>('[data-testid^="board-card-"]');
        if (devourerCard !== null && preyCard !== null) {
          if (!preyCard.classList.contains('cards-board-card-devour-prey')) {
            const vector = cardAbsorptionVector(preyCard.getBoundingClientRect(), devourerCard.getBoundingClientRect());
            preyCard.style.setProperty('--cards-devour-dx', `${vector.dx}px`);
            preyCard.style.setProperty('--cards-devour-dy', `${vector.dy}px`);
            preyCard.classList.add('cards-board-card-devour-prey');
            devourerCard.classList.add('cards-board-card-devourer');
            devourStyledRef.current.push(preyCard, devourerCard);
            // The prey is the end that travels, so it takes the higher lift.
            liftFlight(currentSource, 7, 4);
            liftFlight(currentTarget, 9, 5);
          }
        }
      }
      applyPulseGeometry();
      setPulseMeasured(true);
    };
    updateGeometry();
    // Scroll storms (smooth auto-scroll fires per frame) coalesce into one
    // re-measure per animation frame; the initial measure above stays sync.
    let frame: number | undefined;
    const scheduleUpdate = (): void => {
      frame ??= requestAnimationFrame(() => {
        frame = undefined;
        updateGeometry();
      });
    };
    window.addEventListener('resize', scheduleUpdate);
    window.addEventListener('scroll', scheduleUpdate, true);
    return () => {
      if (frame !== undefined) cancelAnimationFrame(frame);
      window.removeEventListener('resize', scheduleUpdate);
      window.removeEventListener('scroll', scheduleUpdate, true);
      clearLungeVariables();
      clearAbsorptionClasses();
      clearDevourClasses();
      clearFlightLift();
    };
  }, [playback.combatCue, playback.effectPathCue]);

  useEffect(() => {
    const cue = playback.castCue;
    const root = fieldRef.current;
    if (cue === undefined || root === null) {
      castPositionRef.current = undefined;
      setCastMeasured(false);
      return;
    }
    const updatePosition = (): void => {
      const target = cue.targetPlayerId === undefined
        ? cue.targetCellId === undefined
          ? null
          : root.querySelector<HTMLElement>(`[data-testid="cell-${cue.targetCellId}"]`)
        : document.querySelector<HTMLElement>(`[data-player-id="${cue.targetPlayerId}"]`);
      if (target === null) {
        castPositionRef.current = undefined;
        setCastMeasured(false);
        return;
      }
      const rect = target.getBoundingClientRect();
      castPositionRef.current = { left: rect.left + rect.width / 2, top: rect.top + rect.height / 2 };
      applyCastGeometry();
      setCastMeasured(true);
    };
    updatePosition();
    let frame: number | undefined;
    const scheduleUpdate = (): void => {
      frame ??= requestAnimationFrame(() => {
        frame = undefined;
        updatePosition();
      });
    };
    window.addEventListener('resize', scheduleUpdate);
    window.addEventListener('scroll', scheduleUpdate, true);
    return () => {
      if (frame !== undefined) cancelAnimationFrame(frame);
      window.removeEventListener('resize', scheduleUpdate);
      window.removeEventListener('scroll', scheduleUpdate, true);
    };
  }, [playback.castCue]);

  // The portals mount one commit after the anchors were measured (and remount
  // when a cue changes its key); stamp the latest geometry onto the fresh
  // nodes before paint.
  useLayoutEffect(() => {
    applyPulseGeometry();
    applyCastGeometry();
  });

  // FLIP a moved unit from its old cell to the new one. turnPlayback relocates
  // the card in state and sets moveCue in the same commit, so here the card is
  // already in its destination cell; we measure the delta back to the source
  // cell, snap the card there without a transition, then release it so it
  // glides across the field instead of teleporting. Runs before paint, so the
  // eye never sees the card at its destination first.
  useLayoutEffect(() => {
    const cue = playback.moveCue;
    const root = fieldRef.current;
    if (cue === undefined || root === null) return;
    if (typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const fromCell = root.querySelector<HTMLElement>(`[data-testid="cell-${cue.fromCellId}"]`);
    const card = root.querySelector<HTMLElement>(`[data-testid="board-card-${cue.instanceId}"]`);
    if (fromCell === null || card === null) return;
    const fromRect = fromCell.getBoundingClientRect();
    const cardRect = card.getBoundingClientRect();
    const dx = (fromRect.left + fromRect.width / 2) - (cardRect.left + cardRect.width / 2);
    const dy = (fromRect.top + fromRect.height / 2) - (cardRect.top + cardRect.height / 2);
    if (!Number.isFinite(dx) || !Number.isFinite(dy) || Math.hypot(dx, dy) < 1) return;
    // The slide itself is CSS (data-unit-move, 03 §4.10): here only the offset
    // and the two phases — pinned at the old cell, then released home.
    const clear = (): void => {
      clearTimeout(timer);
      card.removeEventListener('transitionend', onEnd);
      card.removeAttribute('data-unit-move');
      card.style.removeProperty('--cards-move-x');
      card.style.removeProperty('--cards-move-y');
    };
    const onEnd = (event: TransitionEvent): void => {
      if (event.target === card && event.propertyName === 'transform') clear();
    };
    card.style.setProperty('--cards-move-x', `${dx}px`);
    card.style.setProperty('--cards-move-y', `${dy}px`);
    card.setAttribute('data-unit-move', 'from');
    void card.offsetWidth; // flush the pre-position so the release animates
    card.setAttribute('data-unit-move', 'to');
    card.addEventListener('transitionend', onEnd);
    // A transform that never changes never ends; the beat still has to let go.
    const timer = setTimeout(clear, TURN_PLAYBACK_TIMING.unitMove * 2);
    return clear;
  }, [playback.moveCue]);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    if (!window.matchMedia('(max-width: 900px), (max-height: 520px) and (pointer: coarse)').matches) return;
    const focusCellId = playback.combatCue?.targetCellId
      ?? playback.effectPathCue?.targetCellId
      ?? playback.castCue?.targetCellId
      ?? playback.cellCue?.cellId
      ?? (
      playback.enteringId === undefined
        ? undefined
        : state.instances[playback.enteringId]?.cellId
    );
    if (focusCellId === undefined) return;
    const scrollRoot = scrollRef.current;
    const target = fieldRef.current?.querySelector<HTMLElement>(`[data-testid="cell-${focusCellId}"]`);
    if (scrollRoot === null || target === null || target === undefined || typeof scrollRoot.scrollTo !== 'function') return;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const rootRect = scrollRoot.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    const centeredLeft = scrollRoot.scrollLeft + targetRect.left - rootRect.left
      - (scrollRoot.clientWidth - targetRect.width) / 2;
    const maxLeft = Math.max(0, scrollRoot.scrollWidth - scrollRoot.clientWidth);
    scrollRoot.scrollTo({
      left: Math.max(0, Math.min(centeredLeft, maxLeft)),
      behavior: reduceMotion ? 'auto' : 'smooth',
    });
  }, [playback.castCue, playback.cellCue, playback.combatCue, playback.effectPathCue, playback.enteringId, playback.spawnCue, playback.assemblyCue, state.instances]);

  // Stable per-cell callbacks so memo(Cell) can compare data props only: each
  // handler dispatches to the latest render's closures through this ref, and
  // its own identity never changes for a given cell.
  const latestCellPropsRef = useRef({ props, targeting, stagedByCell, flights });
  latestCellPropsRef.current = { props, targeting, stagedByCell, flights };
  const cellHandlersRef = useRef(new Map<CellId, {
    onClick: () => void;
    onStagedPointerDragStart: () => void;
    onStagedPointerDrop: (clientX: number, clientY: number) => void;
    onStagedPointerDragEnd: () => void;
    onStagedKeyboardUnplan: () => void;
    onStagedRelease: (motion: Parameters<CardFlightApi['launch']>[1], origin: Parameters<CardFlightApi['launch']>[2]) => void;
  }>());
  function cellHandlers(id: CellId): NonNullable<ReturnType<typeof cellHandlersRef.current.get>> {
    let handlers = cellHandlersRef.current.get(id);
    if (handlers === undefined) {
      const latest = latestCellPropsRef;
      handlers = {
        onClick: () => (latest.current.targeting !== undefined
          ? latest.current.props.onTargetingCellClick(id)
          : latest.current.props.onCellClick(id)),
        onStagedPointerDragStart: () => {
          const stagedInstanceId = latest.current.stagedByCell[id]?.at(-1);
          if (stagedInstanceId !== undefined) latest.current.props.onStagedPointerDragStart(stagedInstanceId);
        },
        onStagedPointerDrop: (clientX, clientY) => {
          const stagedInstanceId = latest.current.stagedByCell[id]?.at(-1);
          if (stagedInstanceId !== undefined) latest.current.props.onStagedPointerDrop(stagedInstanceId, clientX, clientY);
        },
        onStagedPointerDragEnd: () => latest.current.props.onStagedPointerDragEnd(),
        onStagedKeyboardUnplan: () => {
          const stagedInstanceId = latest.current.stagedByCell[id]?.at(-1);
          if (stagedInstanceId !== undefined) latest.current.props.onStagedKeyboardUnplan(stagedInstanceId);
        },
        onStagedRelease: (motion, origin) => {
          const stagedInstanceId = latest.current.stagedByCell[id]?.at(-1);
          if (stagedInstanceId !== undefined) latest.current.flights.launch(stagedInstanceId, motion, origin);
        },
      };
      cellHandlersRef.current.set(id, handlers);
    }
    return handlers;
  }

  function renderCell(id: CellId, isOwn: boolean): ReactElement {
    const { laneIndex } = parseCellId(id);
    const view = cellStateViews.get(id);
    // Every cell the board renders comes from the same TABLE_IDS × LANE_INDEXES ×
    // sides product the pass above walked, so a miss means the two disagree.
    if (view === undefined) throw new Error(`Board: no projected state for cell ${id}`);
    const { visibleOccupantId, instance } = view;
    const plannedInstanceIds = isOwn || props.showOpponentStaged ? stagedByCell[id] ?? [] : [];
    // A resolved plan remains in player.staged until the round-end cleanup.
    // Once its instance has left the hand it must never fall through as a
    // staged preview underneath a departed/dying board occupant.
    const visibleStagedIds = plannedInstanceIds.filter(
      (plannedInstanceId) => state.instances[plannedInstanceId]?.zone === 'hand',
    );
    const stagedInstanceId = visibleStagedIds.at(-1);
    const stagedInstance = stagedInstanceId !== undefined ? state.instances[stagedInstanceId] : undefined;
    const stagedDefinition = stagedInstance === undefined ? undefined : registry.get(stagedInstance.defId);
    // Every card buried under the top one draws its own full face, so each needs
    // its own definition — a pile is only readable if the cards in it say what
    // they are.
    const stagedLayerCards: StagedLayerCardView[] = visibleStagedIds.slice(0, -1).flatMap((layerId) => {
      const layerInstance = state.instances[layerId];
      if (layerInstance === undefined) return [];
      const layerDefinition = registry.get(layerInstance.defId);
      return [{
        instance: layerInstance,
        cardType: layerDefinition?.type,
        cost: layerDefinition?.cost,
        hasStats: layerDefinition?.baseStats !== undefined,
        keywords: layerDefinition?.keywords ?? [],
      }];
    });
    const fizzleInstance = playback.fieldFizzleCue?.cellId === id
      ? state.instances[playback.fieldFizzleCue.instanceId]
      : undefined;
    const fizzleDefinition = fizzleInstance === undefined ? undefined : registry.get(fizzleInstance.defId);

    let highlight = false;
    let dimmed = false;
    const targetingActive = targeting !== undefined;
    const targetingCandidate = targeting?.candidateCells.has(id) === true;
    const targetingSelected = targeting?.selectedCells.has(id) === true;
    const placementId = draggingHandId ?? draggingStagedId ?? selectedHandId;
    if (targetingActive) {
      highlight = targetingCandidate || targetingSelected;
    } else if (placementId !== undefined) {
      const intent = intentForCell(placementId, id);
      highlight = intent.selectable;
      if (canApply !== undefined && intent.action !== undefined) {
        dimmed = !canApply(intent.action);
      }
    }

    return (
      <Cell
        key={id}
        cellId={id}
        instance={instance}
        effective={view.effective}
        cardType={view.cardType}
        keywords={view.keywords}
        // The keyboard-reachable half of "details answer a click": it decides
        // role/tabIndex/aria-*, so it has to be in the render. The carry half
        // rides the context box instead — see ./boardCarry.ts.
        detailsEnabled={selectedHandId === undefined && targeting === undefined}
        rush={view.rush}
        shield={view.shield}
        eligibility={view.eligibility}
        controllerId={view.controllerId}
        viewerId={props.viewerId}
        playerNames={playerNames}
        attachments={view.attachments}
        statuses={view.statuses}
        effectBadges={view.effectBadges}
        reservation={view.reservation}
        stagedInstance={stagedInstance}
        stagedCardType={stagedDefinition?.type}
        stagedCardCost={stagedDefinition?.cost}
        stagedCardHasStats={stagedDefinition?.baseStats !== undefined}
        stagedKeywords={stagedDefinition?.keywords ?? []}
        stagedDragEnabled={!planningDisabled && stagedInstanceId !== undefined}
        stagedLayerCards={stagedLayerCards}
        fizzleInstance={fizzleInstance}
        fizzleCardType={fizzleDefinition?.type}
        fizzleKeywords={fizzleDefinition?.keywords ?? []}
        fizzleLeaving={playback.fieldFizzleCue?.leaving === true}
        highlight={highlight}
        dimmed={dimmed}
        targetSelected={targetingSelected}
        {...(playback.cellCue?.cellId === id ? {
          playbackCue: `${playback.cellCue.kind}-${playback.cellCue.phase}`,
          ...(playback.cellCue.delta === undefined ? {} : { playbackCueDelta: playback.cellCue.delta }),
        } : {})}
        coordinateLabel={`A${laneIndex + 1}`}
        dropEnabled={!planningDisabled && !targetingActive
          && (draggingHandId !== undefined || draggingStagedId !== undefined)
          && highlight && !dimmed}
        interactive={
          !planningDisabled && (
            targetingActive
              ? (targetingCandidate || targetingSelected)
              : (selectedHandId !== undefined && highlight && !dimmed)
          )
        }
        width={CELL_WIDTH}
        {...cellHandlers(id)}
        stagedFlightHidden={stagedInstanceId !== undefined && flights.flyingIds.has(stagedInstanceId)}
        stagedFlightLanding={stagedInstanceId !== undefined && flights.landingIds.has(stagedInstanceId)}
        occupantFlightHidden={visibleOccupantId !== null && flights.flyingIds.has(visibleOccupantId)}
        occupantFlightLanding={visibleOccupantId !== null && flights.landingIds.has(visibleOccupantId)}
      />
    );
  }

  const tablePlacementId = draggingHandId ?? selectedHandId;
  function tableDropState(tableId: TableId): {
    highlight: boolean;
    dimmed: boolean;
    dropEnabled: boolean;
    interactive: boolean;
  } {
    if (tablePlacementId === undefined || planningDisabled) {
      return { highlight: false, dimmed: false, dropEnabled: false, interactive: false };
    }
    const intent = intentForTable(tablePlacementId, tableId);
    const dimmed = canApply !== undefined && intent.action !== undefined ? !canApply(intent.action) : false;
    return {
      highlight: intent.selectable,
      dimmed,
      dropEnabled: draggingHandId !== undefined && intent.selectable && !dimmed,
      interactive: selectedHandId !== undefined && intent.selectable && !dimmed,
    };
  }

  // Named rather than returned inline only so the carry provider can wrap it
  // without re-indenting the whole board.
  const field = (
    <div
      className="cards-board-scroll"
      data-testid="board"
      ref={scrollRef}
    >
      <div
        className="cards-board-field"
        ref={fieldRef}
      >
        {playback.combatCue !== undefined && pulseMeasured && typeof document !== 'undefined' ? createPortal(
          <>
            <div
              className="cards-combat-pulse"
              key={`${playback.combatCue.attackGroupId ?? 'attack'}-${playback.combatCue.partIndex ?? 0}-${playback.combatCue.phase}`}
              data-testid="combat-pulse"
              data-phase={playback.combatCue.phase}
              data-attack-group={playback.combatCue.attackGroupId}
              data-part={playback.combatCue.partIndex === undefined || playback.combatCue.partCount === undefined
                ? undefined
                : `${playback.combatCue.partIndex + 1}/${playback.combatCue.partCount}`}
              aria-hidden="true"
              // Measured combat geometry is per-frame UI state written straight
              // to the node (applyPulseGeometry); visual/timing declarations
              // live in KIT_CSS.
              ref={pulseRef}
            >
              <span />
            </div>
            {playback.combatCue.parallel !== undefined && parallelPulseMeasured ? (
              <div
                className="cards-combat-pulse"
                key={`${playback.combatCue.parallel.attackGroupId ?? 'parallel-attack'}-${playback.combatCue.parallel.partIndex ?? 0}-${playback.combatCue.parallel.phase}`}
                data-testid="combat-pulse-parallel"
                data-phase={playback.combatCue.parallel.phase}
                data-attack-group={playback.combatCue.parallel.attackGroupId}
                data-part={playback.combatCue.parallel.partIndex === undefined || playback.combatCue.parallel.partCount === undefined
                  ? undefined
                  : `${playback.combatCue.parallel.partIndex + 1}/${playback.combatCue.parallel.partCount}`}
                aria-hidden="true"
                ref={parallelPulseRef}
              >
                <span />
              </div>
            ) : null}
            {playback.combatCue.phase === 'impact' ? (
              <>
                <div
                  className="cards-combat-impact-ring"
                  data-harmless={playback.combatCue.amount === 0 ? 'true' : undefined}
                  aria-hidden="true"
                  ref={impactRingRef}
                >
                  <ImpactSparks />
                </div>
                {playback.combatCue.amount > 0 ? (
                  <div
                    className="cards-combat-damage-number"
                    data-testid="combat-damage-number"
                    data-magnitude={damageMagnitude(playback.combatCue.amount)}
                    aria-live="polite"
                    ref={damageNumberRef}
                  >
                    −{playback.combatCue.amount}
                  </div>
                ) : null}
                {playback.combatCue.reciprocal === undefined ? null : (
                  <>
                    <div
                      className="cards-combat-impact-ring"
                      data-harmless={playback.combatCue.reciprocal.amount === 0 ? 'true' : undefined}
                      aria-hidden="true"
                      ref={reciprocalImpactRingRef}
                    >
                      <ImpactSparks />
                    </div>
                    {playback.combatCue.reciprocal.amount > 0 ? (
                      <div
                        className="cards-combat-damage-number"
                        data-testid="combat-damage-number-reciprocal"
                        data-magnitude={damageMagnitude(playback.combatCue.reciprocal.amount)}
                        aria-live="polite"
                        ref={reciprocalDamageNumberRef}
                      >
                        −{playback.combatCue.reciprocal.amount}
                      </div>
                    ) : null}
                  </>
                )}
                {playback.combatCue.parallel === undefined || !parallelPulseMeasured ? null : (
                  <>
                    <div
                      className="cards-combat-impact-ring"
                      data-harmless={playback.combatCue.parallel.amount === 0 ? 'true' : undefined}
                      aria-hidden="true"
                      ref={parallelImpactRingRef}
                    >
                      <ImpactSparks />
                    </div>
                    {playback.combatCue.parallel.amount > 0 ? (
                      <div
                        className="cards-combat-damage-number"
                        data-testid="combat-damage-number-parallel"
                        data-magnitude={damageMagnitude(playback.combatCue.parallel.amount)}
                        aria-live="polite"
                        ref={parallelDamageNumberRef}
                      >
                        −{playback.combatCue.parallel.amount}
                      </div>
                    ) : null}
                  </>
                )}
              </>
            ) : null}
          </>,
          document.body,
        ) : null}
        {playback.effectPathCue !== undefined && pulseMeasured && typeof document !== 'undefined' ? createPortal(
          <div
            className={playback.effectPathCue.kind === 'death-fall' ? 'cards-death-fall-cue' : 'cards-effect-link'}
            data-testid={playback.effectPathCue.kind === 'death-fall' ? 'death-fall-cue' : 'effect-link'}
            data-kind={playback.effectPathCue.kind}
            data-phase={playback.effectPathCue.phase}
            data-amount={playback.effectPathCue.phase === 'impact' && playback.effectPathCue.amount !== undefined
              ? `−${playback.effectPathCue.amount}`
              : undefined}
            aria-hidden="true"
            ref={effectLinkRef}
          >{playback.effectPathCue.kind === 'death-fall' && playback.effectPathCue.sourceDefId !== undefined
            ? <span><CardGlyph defId={playback.effectPathCue.sourceDefId} size={54} /></span>
            : <span />}</div>,
          document.body,
        ) : null}
        <FiberOverlay testId="devour-fibers" links={devourFibers} />
        <FiberOverlay testId="cast-fibers" links={castFibers} />
        {playback.spawnCue?.decayFragment === true && debrisGeometry !== undefined && typeof document !== 'undefined' ? createPortal(
          <div
            className="cards-decay-debris"
            key={playback.spawnCue.targetInstanceIds.join(',')}
            data-testid="decay-debris"
            aria-hidden="true"
            style={{ left: debrisGeometry.left, top: debrisGeometry.top }}
          >
            <span /><span /><span /><span /><span /><span />
            {debrisGeometry.couriers.map((courier, index) => (
              <span
                key={`courier-${index}`}
                data-kind="courier"
                style={{
                  '--cards-debris-dx': `${courier.dx}px`,
                  '--cards-debris-dy': `${courier.dy}px`,
                  width: courier.size,
                  height: courier.size,
                  animationDelay: `${courier.delayMs}ms`,
                } as CSSProperties}
              />
            ))}
          </div>,
          document.body,
        ) : null}
        {playback.castCue !== undefined && castMeasured && typeof document !== 'undefined' ? createPortal(
          <div
            className="cards-cast-cue"
            data-testid="cast-cue"
            data-phase={playback.castCue.phase}
            data-amount={playback.castCue.phase === 'impact' && playback.castCue.amount !== undefined
              ? `−${playback.castCue.amount}`
              : undefined}
            aria-hidden="true"
            ref={castRef}
          >
            <CardGlyph defId={playback.castCue.sourceDefId} size={42} />
          </div>,
          document.body,
        ) : null}
        {props.opponentSpellQueue}
        <div
          className="cards-board-locations"
          data-testid="board-locations"
          data-shake={playback.shake}
        >
          {TABLE_IDS.map((tableId, tableIndex) => {
            const drop = tableDropState(tableId);
            return (
              <section
                key={tableId}
                className="cards-board-location"
                data-testid={`board-location-${tableId}`}
                data-table-id={tableId}
                data-active={drop.highlight ? 'true' : undefined}
                data-dimmed={drop.dimmed ? 'true' : undefined}
                data-table-highlight={drop.highlight ? 'true' : undefined}
                data-table-dimmed={drop.dimmed ? 'true' : undefined}
                data-table-drop-enabled={drop.dropEnabled ? 'true' : undefined}
                data-table-interactive={drop.interactive ? 'true' : undefined}
                onClick={drop.interactive ? () => onTableClick(tableId) : undefined}
              >
                <header className="cards-board-location-header">
                  <span>
                    <span className="cards-location-symbol" aria-hidden="true"><PixelIcon name={LOCATION_SYMBOLS[tableIndex]!} /> </span>
                    {`ЛОКАЦИЯ ${tableId + 1}`}
                  </span>
                </header>
                <div className="cards-board-location-half" data-side="opponent">
                  <LocationGroup
                    tableId={tableId}
                    tableIndex={tableIndex}
                    side="opponent"
                  >
                    {LANE_INDEXES.map((laneIndex) => renderCell(makeCellId(tableId, laneIndex, opponentSide), false))}
                  </LocationGroup>
                </div>
                <div className="cards-board-front-line" aria-hidden="true">
                  <span>VS</span>
                </div>
                <div className="cards-board-location-half" data-side="viewer">
                  <LocationGroup
                    tableId={tableId}
                    tableIndex={tableIndex}
                    side="viewer"
                  >
                    {LANE_INDEXES.map((laneIndex) => renderCell(makeCellId(tableId, laneIndex, viewerSide), true))}
                  </LocationGroup>
                </div>
              </section>
            );
          })}
        </div>
        {props.viewerSpellQueue}
      </div>
    </div>
  );
  return <BoardCarryContext.Provider value={carryGate}>{field}</BoardCarryContext.Provider>;
}
